// LDF: Social-Media-Links vereinheitlichen – ein echtes Profil pro Plattform, keine Rechts-/Teilen-Seiten.
// Gleiche Logik liegt in supabase/functions/_shared/socialLinks.ts (Edge Function).

const PLATFORMS: { key: string; host: RegExp }[] = [
  { key: "instagram", host: /(^|\.)instagram\.com$/ },
  { key: "facebook", host: /(^|\.)(facebook\.com|fb\.com)$/ },
  { key: "x", host: /(^|\.)(twitter\.com|x\.com)$/ },
  { key: "linkedin", host: /(^|\.)linkedin\.com$/ },
  { key: "youtube", host: /(^|\.)(youtube\.com|youtu\.be)$/ },
  { key: "tiktok", host: /(^|\.)tiktok\.com$/ },
  { key: "xing", host: /(^|\.)xing\.com$/ },
  { key: "pinterest", host: /(^|\.)pinterest\.[a-z.]+$/ },
  { key: "threads", host: /(^|\.)threads\.net$/ },
  { key: "vimeo", host: /(^|\.)vimeo\.com$/ },
];

// Pfade, die kein Profil sind (Richtlinien, Teilen, Login, einzelne Beiträge …)
const NON_PROFILE =
  /^(about|accounts?|explore|legal|policy|policies|privacy|terms|tos|help|login|signup|share|sharer(\.php)?|intent|dialog|plugins|tr|home|hashtag|search|p|reel|reels|stories|watch|embed|shorts|status|events|groups|pages\/create|business|ads|developers?|cookies?|settings|notifications|messages)$/i;

export const socialPlatform = (url: string) => {
  try {
    const host = new URL(url.startsWith("http") ? url : `https://${url}`).hostname.toLowerCase();
    return PLATFORMS.find((p) => p.host.test(host))?.key ?? null;
  } catch {
    return null;
  }
};

// Kanonische Profil-URL oder null, wenn es kein Profil ist
export const canonicalSocial = (url: string): string | null => {
  let u: URL;
  try {
    u = new URL(url.trim().startsWith("http") ? url.trim() : `https://${url.trim()}`);
  } catch {
    return null;
  }
  const platform = socialPlatform(u.toString());
  if (!platform) return null;
  const segments = u.pathname.split("/").filter(Boolean).map((s) => decodeURIComponent(s));
  if (!segments.length) return null;
  let path: string[];
  switch (platform) {
    case "linkedin":
      if (!["company", "in", "school", "showcase"].includes(segments[0]) || !segments[1]) return null;
      path = [segments[0], segments[1]];
      break;
    case "youtube":
      if (segments[0].startsWith("@")) path = [segments[0]];
      else if (["channel", "c", "user"].includes(segments[0]) && segments[1]) path = [segments[0], segments[1]];
      else return null;
      break;
    case "xing":
      if (!["pages", "companies"].includes(segments[0]) || !segments[1]) return null;
      path = [segments[0], segments[1]];
      break;
    case "facebook":
      if (segments[0] === "profile.php") {
        const id = u.searchParams.get("id");
        if (!id) return null;
        return `https://www.facebook.com/profile.php?id=${id}`;
      }
      if (NON_PROFILE.test(segments[0])) return null;
      path = [segments[0]];
      break;
    default:
      if (NON_PROFILE.test(segments[0].replace(/^@/, ""))) return null;
      path = [segments[0]];
  }
  const hosts: Record<string, string> = {
    instagram: "www.instagram.com",
    facebook: "www.facebook.com",
    x: "x.com",
    linkedin: "www.linkedin.com",
    youtube: "www.youtube.com",
    tiktok: "www.tiktok.com",
    xing: "www.xing.com",
    pinterest: "www.pinterest.de",
    threads: "www.threads.net",
    vimeo: "vimeo.com",
  };
  return `https://${hosts[platform]}/${path.map((p) => encodeURIComponent(p).replace(/%40/g, "@")).join("/")}`;
};

// Alle Links bereinigen: Nicht-Social-Links bleiben (dedupliziert), Socials einmal pro Plattform
export const cleanContextLinks = (links: (string | null | undefined)[], linkedinUrl?: string | null) => {
  const out: string[] = [];
  const seenPlatforms = new Set<string>();
  const seen = new Set<string>();
  if (linkedinUrl && canonicalSocial(linkedinUrl)) seenPlatforms.add("linkedin");
  for (const raw of links) {
    if (!raw || !raw.trim()) continue;
    const platform = socialPlatform(raw);
    if (platform) {
      const canonical = canonicalSocial(raw);
      if (!canonical || seenPlatforms.has(platform)) continue;
      seenPlatforms.add(platform);
      out.push(canonical);
    } else {
      const key = raw.trim().replace(/\/+$/, "").replace(/^https?:\/\/(www\.)?/i, "").toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(raw.trim());
    }
  }
  return out;
};
